import { BroadcastEvent } from '@formbird/types';
import { Observable, ReplaySubject, forkJoin, from, of, firstValueFrom } from 'rxjs';
import { map, filter } from 'rxjs/operators';

/**
 * A handler registered via {@link BroadcastService.registerUpdatedFieldHandler} so its
 * outcome can be awaited via {@link BroadcastService.onUpdatedFieldProcessed}.
 */
type UpdatedFieldHandler<T = unknown, R = unknown> = (data: T) => Promise<R> | R;

/**
 * The outcome a field-updated handler reports back for its field.
 */
export interface UpdatedFieldResult {
  newValue: any[];
  conflicts: any[];
  processed: boolean;
}

export class BroadcastService {
  private _eventBus: ReplaySubject<BroadcastEvent>;
  private _updatedFieldHandlers = new Map<string, Map<string, UpdatedFieldHandler<any, any>[]>>();

  constructor() {
    this._eventBus = new ReplaySubject<BroadcastEvent>();
  }

  broadcast(key: any, data?: any) {
    this._eventBus.next({ key, data });
  }

  on<T>(key: any): Observable<T> {
    return this._eventBus
      .pipe(filter(event => event.key === key))
      .pipe(map(event => <T>event.data));
  }

  /**
   * Registers an async (or sync) handler for a specific field of a specific template so its
   * completion/result can be awaited by {@link onUpdatedFieldProcessed} when that field changes.
   * Multiple handlers may be registered for the same template/field pair; all of them are invoked.
   * @param templateId - The template the field belongs to.
   * @param fieldName - The template field this handler responds to.
   * @param handler - Function invoked with the broadcast payload; may return a Promise.
   * @example
   * ```typescript
   * broadcastService.registerUpdatedFieldHandler(templateId, 'someField', async (data) => {
   *   ...
   * });
   * ```
   * @since 1.0.0
   */
  registerUpdatedFieldHandler<T = unknown>(templateId: string, fieldName: string, handler: UpdatedFieldHandler<T, UpdatedFieldResult>): void {
    const fieldHandlers = this._updatedFieldHandlers.get(templateId) ?? new Map<string, UpdatedFieldHandler<any, any>[]>();
    const handlers = fieldHandlers.get(fieldName) ?? [];
    handlers.push(handler as UpdatedFieldHandler<any, any>);
    fieldHandlers.set(fieldName, handlers);
    this._updatedFieldHandlers.set(templateId, fieldHandlers);
  }

  /**
   * Emits once every handler registered for this template/field pair via
   * {@link registerUpdatedFieldHandler} has completed, then completes.
   * @param data - Payload passed to each handler; its `templateId` and `modifiedField` select
   * which registered handlers are invoked.
   * @returns A Promise resolving to the results returned by each handler, in registration order.
   * @throws Rejects if any handler throws or returns a rejected Promise.
   * @example
   * ```typescript
   * const results = await broadcastService.onUpdatedFieldProcessed(data);
   * ```
   * @since 1.0.0
   */
  onUpdatedFieldProcessed<T extends { templateId: string; modifiedField: string } = any, R = UpdatedFieldResult>(data: T): Promise<R[]> {
    const handlers = this._updatedFieldHandlers.get(data.templateId)?.get(data.modifiedField) ?? [];
    if (!handlers.length) {
      return firstValueFrom(of([] as R[]));
    }

    return firstValueFrom(forkJoin(handlers.map(handler => from(Promise.resolve(handler(data))))) as Observable<R[]>);
  }
}
